import { DatePipe } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { Subject, of } from "rxjs";
import { catchError, take, takeUntil } from "rxjs/operators";
import { BroadcastEventTypes } from "src/app/core/models/lookup.model";
import { Patient } from "src/app/core/models/patient.model";
import { ApplicationCacheService } from "src/app/core/services/application-cache.service";
import { ApplicationContextService } from "src/app/core/services/application-context.service";
import { EngagePatientService } from "src/app/core/services/engage-patient.service";
import { EventService } from "src/app/core/services/event.service";@Component({
  selector: "app-patient-info-header",
  templateUrl: "./patient-info-header.component.html",
  styleUrls: ["./patient-info-header.component.scss"],
})
export class PatientInfoHeaderComponent implements OnInit, OnDestroy {
  patient: Patient;
  physicianName: string;
  checkInTime: string;
  /** Resolved Engage referral UUID for assignment PATCH / details GET (`GET …/referrals/{id}/details`). */
  engageReferralId: string | null = null;
  assignedToOptionsForAssignment: Array<{ name: string; value: string }> = [];
  selectedAssignedToForAssignment: { name: string; value: string } | null = null;
  isUpdatingAssignment = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly _applicationCacheService: ApplicationCacheService,
    private readonly _eventService: EventService,
    private readonly _datePipe: DatePipe,
    private readonly _appContextService: ApplicationContextService,
    private readonly _engagePatientService: EngagePatientService) {
    this._eventService
      .on(BroadcastEventTypes.ScheduleCacheUpdated)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateCheckInTime();
      });
    this._eventService
      .on(BroadcastEventTypes.PatientInfoUpdated)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.patient = this._applicationCacheService.applicationCache.patient;
      });
    this._eventService
      .on(BroadcastEventTypes.EngageReferralAssignmentChanged)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const next =
          this._applicationCacheService.applicationCache.engageNotesReferralId || null;
        if (next === this.engageReferralId) {
          return;
        }
        this.engageReferralId = next;
        this.loadAssignedToFromReferral();
      });
    this._eventService
      .on(BroadcastEventTypes.AppointmentUpdated)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.physicianName =
          this._applicationCacheService.applicationCache.appointment.providerName;
      });
  }

  ngOnInit() {
    this.patient = this._applicationCacheService.applicationCache.patient;
    this.physicianName =
      this._applicationCacheService.applicationCache.appointment.providerName;
    this.updateCheckInTime();

    this.engageReferralId =
      this._applicationCacheService.applicationCache.engageNotesReferralId || null;

    // Build dropdown options from context users.
    this._appContextService.context.pipe(takeUntil(this.destroy$)).subscribe({
      next: (context) => {
        const users = context?.allAvailableUsers || [];
        this.assignedToOptionsForAssignment = [
          { name: "Everyone", value: "" },
          ...users
            .filter((u) => u?.id && String(u.id).trim())
            .map((u) => ({
              name: `${u.firstName || ""} ${u.lastName || ""}`.trim() || "Unknown",
              value: String(u.id || "").trim(),
            })),
        ];
      },
      error: () => {
        this.assignedToOptionsForAssignment = [{ name: "Everyone", value: "" }];
      },
    });

    // Load current assigned_to from referral details.
    this.loadAssignedToFromReferral();
  }

  private loadAssignedToFromReferral(): void {
    const rid = (this.engageReferralId || "").trim();
    if (!rid) {
      this.selectedAssignedToForAssignment = null;
      return;
    }
    const pid = (this.patient?.id || "").trim();
    this._engagePatientService.getReferralDetailsById(pid, rid).subscribe({
      next: (details: any) => {
        const assignedTo = (details?.assigned_to || details?.assignedTo || "").toString().trim();
        if (!assignedTo) {
          this.selectedAssignedToForAssignment = { name: "Everyone", value: "" };
          return;
        }
        this._engagePatientService.getUserById(assignedTo).subscribe({
          next: (user: any) => {
            if (user?.id) {
              this.selectedAssignedToForAssignment = {
                name: `${user.firstName || ""} ${user.lastName || ""}`.trim() || "Everyone",
                value: String(user.id).trim(),
              };
            } else {
              this.selectedAssignedToForAssignment = { name: "Everyone", value: "" };
            }
          },
          error: () => {
            this.selectedAssignedToForAssignment = { name: "Everyone", value: "" };
          },
        });
      },
      error: () => {
        this.selectedAssignedToForAssignment = { name: "Everyone", value: "" };
      },
    });
  }

  onAssignedToChange(): void {
    const rid = (this.engageReferralId || "").trim();
    if (!rid) return;
    const userId = this.selectedAssignedToForAssignment?.value || null;
    this.isUpdatingAssignment = true;
    const pid = (this.patient?.id || "").trim();
    this._engagePatientService.updateReferralAssignment(rid, userId, pid).subscribe({
      next: () => {
this.loadAssignedToFromReferral();
      },
      error: (err: any) => {
this.loadAssignedToFromReferral();
      },
    }).add(() => {
      this.isUpdatingAssignment = false;
    });
  }

  updateCheckInTime() {
    const { checkIn, scheduleDate } =
      this._applicationCacheService.applicationCache.schedule || {};
    if (!checkIn && !scheduleDate) {
      this.checkInTime = "";
      return;
    }
    const formattedDate = scheduleDate
      ? this._datePipe.transform(scheduleDate, "MM/dd/yyyy")
      : "";
    this.checkInTime = `${formattedDate} ${checkIn || ""}`;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get selectedEncounter() {
    return this._applicationCacheService.applicationCache;
  }
}
